import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  ApiResponse,
  ChatRequest,
  ChatResponse,
  ChatSession,
  ChatMessage,
  WllUser,
} from '../types';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

// ── Auth token helper ─────────────────────────────────────────────────────────

async function getIdToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No auth token available — please sign in');
  return token;
}

// ── Base fetch wrapper ────────────────────────────────────────────────────────

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  requiresAuth = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (requiresAuth) {
    const token = await getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  } catch (networkErr) {
    // fetch() rejects on network failure, DNS, CORS preflight block, or when a
    // Lambda crashes/times out and the gateway returns no CORS headers. The
    // browser surfaces all of these as a generic TypeError, so give the user
    // something more useful than "Failed to fetch".
    throw new ApiError(
      'Could not reach the server. This can happen if the request timed out or ' +
        'the service is temporarily unavailable. Please try again in a moment.',
      'NETWORK_ERROR',
      0,
      { cause: networkErr instanceof Error ? networkErr.message : String(networkErr) },
    );
  }

  // Handle 204 No Content (e.g. DELETE responses)
  if (response.status === 204) {
    return undefined as T;
  }

  // The body may not be JSON if an infrastructure layer (gateway/Lambda crash)
  // returned a raw error. Parse defensively.
  let json: ApiResponse<T> | null = null;
  try {
    json = (await response.json()) as ApiResponse<T>;
  } catch {
    json = null;
  }

  if (!response.ok || !json || !json.success) {
    const errBody = json as { success: false; error?: { code?: string; message?: string; details?: unknown }; requestId?: string } | null;
    throw new ApiError(
      errBody?.error?.message ?? `Request failed with HTTP ${response.status}`,
      errBody?.error?.code ?? `HTTP_${response.status}`,
      response.status,
      errBody?.error?.details,
      errBody?.requestId,
    );
  }

  return (json as { success: true; data: T }).data;
}

// ── Typed API error ───────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  getMe: () => apiFetch<WllUser>('/auth/me'),
};

// ── Users ─────────────────────────────────────────────────────────────────────

export const usersApi = {
  list: () => apiFetch<WllUser[]>('/users'),
  getById: (userId: string) => apiFetch<WllUser>(`/users/${userId}`),
  update: (userId: string, updates: Partial<WllUser>) =>
    apiFetch<WllUser>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
};

// ── Chat ──────────────────────────────────────────────────────────────────────

export const chatApi = {
  sendMessage: (req: ChatRequest) =>
    apiFetch<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  listSessions: () => apiFetch<ChatSession[]>('/chat/sessions'),

  createSession: () =>
    apiFetch<ChatSession>('/chat/sessions', { method: 'POST' }),

  getSession: (sessionId: string) =>
    apiFetch<ChatSession>(`/chat/sessions/${sessionId}`),

  deleteSession: (sessionId: string) =>
    apiFetch<void>(`/chat/sessions/${sessionId}`, { method: 'DELETE' }),

  getMessages: (sessionId: string) =>
    apiFetch<ChatMessage[]>(`/chat/sessions/${sessionId}/messages`),
};

// ── (Upload and Health APIs removed - not used in current UI) ─────────────────
